import * as React from 'react'
import { useCallback, useState } from 'react'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { ViewNotesModal } from '@user/components/Notes/ViewNotesModal/ViewNotesModal'
import { AddNoteDrawer } from '@user/components/Notes'
import { EditNoteDrawer } from '@user/components/Notes/EditNoteDrawer/EditNoteDrawer'
import { useLoadEntityLocationsOnMount } from '@user/store/entityLocations'

export interface EntityNotesFlowProps {
  entityId: number
  entityType: ENTITY_TYPES
  externalLocationId: string
  canUpdate: boolean
  detail: any

  // Control props for the main entry points
  addOpen: boolean
  viewOpen: boolean
  onCloseAdd: () => void
  onCloseView: () => void
  onOpenAdd: () => void
  onOpenView: () => void
  currentServiceLocation?: LocationDTO
}

export const EntityNotesFlow: React.FC<EntityNotesFlowProps> = (props) => {
  const {
    entityId,
    entityType,
    externalLocationId,
    canUpdate,
    detail,
    addOpen,
    viewOpen,
    onCloseAdd,
    onCloseView,
    onOpenAdd,
    onOpenView,
    currentServiceLocation,
  } = props

  const [backToNotes, setBackToNotes] = useState<boolean>(false)
  const [noteId, setNoteId] = useState<number>(-1)
  const [editNoteDrawerOpen, setEditNoteDrawerOpen] = useState<boolean>(false)

  const onNoteAdd = useCallback(() => {
    setBackToNotes(true)
    onOpenAdd()
  }, [onOpenAdd])

  const onGoBack = useCallback(() => {
    onCloseAdd()
    if (backToNotes) {
      onOpenView()
    }
  }, [backToNotes, onCloseAdd, onOpenView])

  const onGoBackFromEdit = useCallback(() => {
    setEditNoteDrawerOpen(false)
    onOpenView()
  }, [setEditNoteDrawerOpen, onOpenView])

  const onEditNoteClose = useCallback(() => {
    setEditNoteDrawerOpen(false)
  }, [setEditNoteDrawerOpen])

  const onSetBackNotes = useCallback(
    (value: boolean, id: number) => {
      if (value) {
        onCloseView()
        setEditNoteDrawerOpen(true)
      } else {
        onOpenView()
        setEditNoteDrawerOpen(false)
      }
      setNoteId(id)
    },
    [setNoteId, setEditNoteDrawerOpen, onCloseView, onOpenView],
  )

  const locationsStatus = useLoadEntityLocationsOnMount(
    entityId,
    entityType,
    viewOpen,
  )

  return (
    <>
      <AddNoteDrawer
        entityId={entityId}
        externalLocationId={externalLocationId}
        entityType={entityType}
        onClose={onCloseAdd}
        onGoBack={onGoBack}
        open={addOpen}
        backToNotes={backToNotes}
        currentServiceLocation={currentServiceLocation}
      />
      <ViewNotesModal
        entityId={entityId}
        entityType={entityType}
        onClose={onCloseView}
        open={viewOpen}
        externalLocationId={externalLocationId}
        canUpdate={canUpdate}
        detail={detail}
        onNoteAdd={onNoteAdd}
        backToNotes={true}
        setBackToNotes={onSetBackNotes}
        locationsStatus={locationsStatus}
      />

      <EditNoteDrawer
        entityId={entityId}
        entityType={entityType}
        noteId={noteId}
        onClose={onEditNoteClose}
        onUpdateSuccess={onEditNoteClose}
        open={editNoteDrawerOpen}
        externalLinkId={externalLocationId}
        backToNotes={true}
        onGoBack={onGoBackFromEdit}
      />
    </>
  )
}
